import type { ConversationId } from "@redotech/effect-api-shared/ids/definitions";
import { integrationTypeToChannel } from "@redotech/effect-api-shared/models/shopper-ai/conversation/channel-utils";
import type { MessageChannel } from "@redotech/effect-api-shared/models/shopper-ai/conversation/message";
import { eq } from "@tanstack/db";
import { useLiveQuery } from "@tanstack/react-db";
import { useMemo } from "react";
import { useGlobalConversationCollections } from "../providers/support-providers";

/**
 * Returns the channels associated with a conversation derived from its threads.
 * Maps each thread's integrationType to the corresponding MessageChannel.
 *
 * @param conversationId - The conversation ID to get channels for
 * @returns Array of unique channels for the conversation
 */
export function useChannelInConversation(
  conversationId: ConversationId,
): MessageChannel[] {
  const collections = useGlobalConversationCollections();

  const threadsResult = useLiveQuery(
    (q) =>
      q
        .from({ thread: collections.threads })
        .where(({ thread }) => eq(thread.conversationId, conversationId))
        .select(({ thread }) => ({ integrationType: thread.integrationType }))
        .distinct(),
    [conversationId],
  );

  return useMemo(() => {
    const channels: MessageChannel[] = [];
    for (const { integrationType } of threadsResult.data) {
      const channel = integrationTypeToChannel[integrationType];
      if (channel !== undefined) {
        channels.push(channel);
      }
    }
    return channels;
  }, [threadsResult.data]);
}
